import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService, MissionJobRead, MissionJobTypeEnum, OdysseyRead, RobotRead } from 'api/schema'
import { Button as CustomButton } from 'components/Button/Button'
import Title from 'components/Title/Title'
import addDays from 'date-fns/addDays'
import endOfDay from 'date-fns/endOfDay'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import enUS from 'date-fns/locale/en-US'
import parse from 'date-fns/parse'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import { useUser } from 'hooks/user'
import Scenario from 'pages/Scenario/Scenario'
import React from 'react'
import { useCallback, useState } from 'react'
import { useRef } from 'react'
import { Calendar, View, Views, dateFnsLocalizer } from 'react-big-calendar'
import { useTranslation } from 'react-i18next'
import robot from 'store/actions/robot'
import { MISSION_JOB_STATUSES, PATH } from 'utils/constants'
import { getVendorDisplayName } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import { ConfirmationDialogAllSingle } from './ConfirmationDialogAllSingle'
import { EventEditDeletePopup } from './TaskSchedulerEventEditDeletePopup'
import { exportScheduleToCSV } from './TaskSchedulerExport'
import {
  ProcessedEvent,
  addEventsStatistics,
  getColorByColorName,
  getColorById,
  getLoadingStatus,
  getTaskId,
  getTaskPrefix,
  renderMissions,
  renderScenario,
  renderStatsTable,
  scheduleToEvents,
  toISOStringYYYYMMDD,
} from './TaskSchedulerUtils'
import TaskSettingsModal from './TaskSettingsModal/TaskSettingsModal'

import schedulerStyle from './SchedulerSettings.module.css'
import style from 'components/Button/Button.module.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

enum EventScopes {
  ALL = 'all',
  CURRENT = 'current',
}

const TaskEventViewer = ({ event }: { event: any }) => {
  const [selectedReport, setSelectedReport] = useState<string>('Overall')

  const extendedProps = event.extendedProps
  const hasExtendedProps =
    extendedProps && extendedProps.missions_progress && extendedProps.missions_progress.length > 0

  const handleReportChange = (event: SelectChangeEvent) => {
    setSelectedReport(event.target.value as string)
  }

  const eventMissions = event.extendedProps?.missions
    ? event.extendedProps.missions
    : event.extendedProps?.mission_id
    ? [event.extendedProps.mission_id]
    : []

  let mainDescription = event.description

  if (hasExtendedProps) {
    if (selectedReport !== 'Overall') {
      mainDescription = ''
    }
  }

  const currentStats =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress
      : extendedProps.missions_progress[Number(selectedReport)])

  const currentName =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress.missions?.length === 1
        ? extendedProps.missions_progress[0]?.name
        : ''
      : extendedProps.missions_progress[Number(selectedReport)]?.name)

  const startTime = format(event.start, 'h:mm a')
  const endTime = format(event.end, 'h:mm a')
  const dateStr = format(event.start, 'EEEE, MMM do')

  return (
    <div style={{ whiteSpace: 'pre-line' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
          <CalendarTodayIcon sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {dateStr}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
          <AccessTimeIcon sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {startTime} — {endTime}
          </Typography>
        </Box>
      </Box>

      {hasExtendedProps && eventMissions.length > 1 && (
        <Box sx={{ minWidth: 120, mb: 1, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-select-label">Report</InputLabel>
            <Select
              labelId="report-select-label"
              id="report-select"
              value={selectedReport}
              label="Report"
              onChange={handleReportChange}
            >
              <MenuItem value={'Overall'}>Overall</MenuItem>
              {extendedProps.missions_progress.map((mission: any, index: number) => (
                <MenuItem key={`${mission.id}_${index}`} value={index.toString()}>
                  Mission {mission.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {mainDescription && <Box sx={{ fontSize: '0.875rem', mb: 0 }}>{mainDescription}</Box>}
      {selectedReport === 'Overall' && renderMissions(eventMissions)}

      {currentStats && (
        <Box>
          {currentName && (
            <Typography variant="body2" sx={{ mb: 0.0 }}>
              <span style={{ fontWeight: 'bold' }}>Mission name:</span> {currentName}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Report:
          </Typography>
          {renderStatsTable(currentStats)}
        </Box>
      )}

      {hasExtendedProps && selectedReport !== 'Overall' && (
        <Box sx={{ mt: 1 }}>
          <a
            className={schedulerStyle.link}
            href={`${PATH.MISSION}${extendedProps.missions_progress[Number(selectedReport)]?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Go to mission
          </a>
        </Box>
      )}
    </div>
  )
}

const TriggerEdit = ({ onClose, onEdit }: { onClose: () => void; onEdit: () => void }) => {
  React.useEffect(() => {
    onEdit()
    onClose()
  }, [onClose, onEdit])
  return null
}

const buildRobotIdToFacilityIds = (facilities: { id: number; robots_ids?: number[] }[]): Map<number, number[]> => {
  const m = new Map<number, number[]>()
  for (const f of facilities) {
    for (const rid of f.robots_ids || []) {
      if (!m.has(rid)) m.set(rid, [])
      m.get(rid)!.push(f.id)
    }
  }
  return m
}

const TaskScheduler = () => {
  const [tasks, setTasks] = React.useState<MissionJobRead[]>([])
  const [odysseys, setOdysseys] = React.useState<OdysseyRead[]>([])
  const [robots, setRobots] = React.useState<RobotRead[]>([])
  const [eventScope, setEventScope] = React.useState<string>(EventScopes.CURRENT)
  const [refreshTaskScheduleCount, setRefreshTaskScheduleCount] = React.useState(0)
  const [calendarEvents, setCalendarEvents] = React.useState<ProcessedEvent[]>([])
  const calendarEventsRef = React.useRef<ProcessedEvent[]>([])

  const [filterFacilityId, setFilterFacilityId] = useState<string>('all')
  const [filterVendor, setFilterVendor] = useState<'all' | VendorCode>('all')
  const [filterRobot, setFilterRobot] = useState<any | null>(null)
  const [filterMission, setFilterMission] = useState<any | null>(null)
  const [filterStartAfter, setFilterStartAfter] = useState<number>(-1)

  // Keep ref in sync with state
  React.useEffect(() => {
    calendarEventsRef.current = calendarEvents
  }, [calendarEvents])

  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date())
  const { t } = useTranslation()
  const [view, setView] = React.useState<View>(Views.MONTH)
  const [currentRange, setCurrentRange] = React.useState<{ start: Date; end: Date }>(() => {
    const now = new Date()
    return {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }
  })

  const updateRange = useCallback(
    (date: Date, currentView: View) => {
      let start: Date, end: Date
      if (currentView === Views.MONTH) {
        start = startOfMonth(date)
        end = endOfMonth(date)
      } else if (currentView === Views.AGENDA) {
        start = startOfDay(date)
        end = addDays(date, 1)
      } else if (currentView === Views.WEEK) {
        start = startOfWeek(date)
        end = endOfWeek(date)
      } else {
        start = startOfDay(date)
        end = endOfDay(date)
      }

      // Only update if actually different to prevent loops
      setCurrentRange(prev => {
        if (prev.start.getTime() === start.getTime() && prev.end.getTime() === end.getTime()) {
          return prev
        }
        return { start, end }
      })
    },
    [setCurrentRange]
  )

  const handleViewChange = useCallback(
    (newView: View) => {
      setView(newView)
      updateRange(selectedDate, newView)
    },
    [selectedDate, updateRange]
  )

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate)
      updateRange(newDate, view)
    },
    [view, updateRange]
  )
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isEditConfirmationOpen, setIsEditConfirmationOpen] = useState(false)
  const [isSingleTaskEditing, setSingleTaskEditing] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ProcessedEvent | null>(null)
  const [openDeleteScheduleDialog, setOpenDeleteScheduleDialog] = React.useState(false)

  const getTaskById = useCallback(
    (eventId: string): MissionJobRead | OdysseyRead | undefined => {
      const prefix = getTaskPrefix(eventId)
      const taskId = getTaskId(eventId)
      if (prefix === 'task') return tasks.find((task: MissionJobRead) => task.id === taskId)
      if (prefix === 'odyssey') return odysseys.find((odyssey: OdysseyRead) => odyssey.id === taskId)
      console.error(`Failed to find a task. Unknown prefix ${prefix}`)
    },
    [tasks, odysseys]
  )

  const isAllValueDisabled = (selectedEvent: ProcessedEvent | null): boolean => {
    if (!selectedEvent) return true
    const task = getTaskById(selectedEvent.event_id as string)
    if (!task || !task.scheduler_settings || !task.scheduler_settings.weekdays) return true

    const weekdays = task.scheduler_settings.weekdays
    const hasWeekdays = Object.values(weekdays).some(val => val === 'true')
    return !hasWeekdays
  }
  const onSettingsCloseClicked = useCallback(
    (refresh: boolean) => {
      setIsSettingsOpen(false)
      setSelectedEvent(null)
    },
    [setIsSettingsOpen, setSelectedEvent]
  )

  const onSettingsSubmitClicked = useCallback(
    (refresh: boolean) => {
      setIsSettingsOpen(false)
      setSelectedEvent(null)
      if (refresh) {
        setRefreshTaskScheduleCount(prevKey => prevKey + 1) // Increment refreshKey to trigger re-render
      }
    },
    [setIsSettingsOpen, setSelectedEvent]
  )

  const [eventsLoaded, setEventsLoaded] = useState(false)
  const isLoadingEvents = useRef(false)
  // Cache data to avoid refetching
  const dataCache = useRef({
    robots: [] as RobotRead[],
    missionsMap: new Map<number, string>(),
    facilities: [] as { id: number; name?: string | null; robots_ids?: number[] }[],
    robotIdToFacilityIds: new Map<number, number[]>(),
    fetched: { tasks: false, odysseys: false, robots: false, missions: false, facilities: false },
  })

  const buildRobotIdToFacilityIds = (facilities: { id: number; robots_ids?: number[] }[]): Map<number, number[]> => {
    const m = new Map<number, number[]>()
    for (const f of facilities) {
      for (const rid of f.robots_ids || []) {
        if (!m.has(rid)) m.set(rid, [])
        m.get(rid)!.push(f.id)
      }
    }
    return m
  }

  // Fetch initial data if not already cached
  const fetchInitialData = useCallback(async (force: boolean = false) => {
    const promises = []

    if (force || !dataCache.current.fetched.robots) {
      promises.push(
        MainService.mainRobotList(['-id'], 1, 1000).then(res => {
          dataCache.current.robots = res.results ?? []
          setRobots(dataCache.current.robots)
          setTasks(dataCache.current.robots.flatMap(robot => robot.missions_jobs))
          setOdysseys(dataCache.current.robots.flatMap(robot => robot.odysseys))
          dataCache.current.fetched.robots = true
        })
      )
    }

    if (force || !dataCache.current.fetched.missions) {
      promises.push(
        MainService.mainMissionList(['-id'], 1, 1000).then(res => {
          const map = new Map<number, string>()
          res.results?.forEach(m => {
            if (m.name) map.set(m.id, m.name)
          })
          dataCache.current.missionsMap = map
          dataCache.current.fetched.missions = true
        })
      )
    }

    if (force || !dataCache.current.fetched.facilities) {
      promises.push(
        MainApi.fetchFacilities(1, 1000).then(res => {
          dataCache.current.facilities = res.results ?? []
          dataCache.current.robotIdToFacilityIds = buildRobotIdToFacilityIds(dataCache.current.facilities)
          dataCache.current.fetched.facilities = true
        })
      )
    }

    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }, [])

  // Generate events from robot data
  const generateEventsFromRobots = useCallback((start: Date, end: Date): ProcessedEvent[] => {
    const eventsSet: ProcessedEvent[] = []

    for (const robot of dataCache.current.robots) {
      const facilityIds = dataCache.current.robotIdToFacilityIds.get(robot.id) ?? []
      // Generate events from missions_jobs
      for (const task of robot.missions_jobs) {
        if (task.job_type === MissionJobTypeEnum.DELETE_SCENARIO) continue

        const loadingStatus = task.status === 'scenario_done' ? '' : getLoadingStatus(task.status)

        const events = scheduleToEvents(
          start,
          end,
          task,
          robot.robot_color ? () => getColorByColorName(robot.robot_color || 'White') : () => getColorById(robot.id),
          <React.Fragment>{loadingStatus && <div>{loadingStatus}</div>}</React.Fragment>,
          robot,
          dataCache.current.missionsMap,
          facilityIds
        )
        eventsSet.push(...events)
      }

      // Generate events from odysseys
      for (const odyssey of robot.odysseys) {
        if (odyssey.job_type === MissionJobTypeEnum.DELETE_SCENARIO) continue

        const loadingStatus = odyssey.status === 'scenario_done' ? '' : getLoadingStatus(odyssey.status)
        const odysseyEvents = scheduleToEvents(
          start,
          end,
          odyssey,
          robot.robot_color ? () => getColorByColorName(robot.robot_color || 'White') : () => getColorById(robot.id),
          <React.Fragment>
            {loadingStatus && <div>{loadingStatus}</div>}
            {odyssey?.scenario ? renderScenario(odyssey.scenario) : null}
          </React.Fragment>,
          robot,
          dataCache.current.missionsMap,
          facilityIds
        )
        eventsSet.push(...odysseyEvents)
      }
    }

    return eventsSet
  }, [])

  const prevRefreshCount = useRef(refreshTaskScheduleCount)
  const getRemoteEvents = React.useCallback(
    async (start: Date, end: Date) => {
      const isManualRefresh = prevRefreshCount.current !== refreshTaskScheduleCount
      prevRefreshCount.current = refreshTaskScheduleCount

      // Prevent duplicate/concurrent execution
      if (isLoadingEvents.current) {
        return []
      }

      isLoadingEvents.current = true

      try {
        // Fetch initial data once (or on manual refresh)
        await fetchInitialData(isManualRefresh)

        // Add a 7-day buffer to the fetch range to ensure visible days from adjacent months are loaded
        const fetchStart = addDays(start, -7)
        const fetchEnd = addDays(end, 7)
        // Fetch event statistics
        const [responseEventStatistics, responseOdysseyStatistics] = await Promise.all([
          MainService.getEventStatisticsByDateRange(
            format(fetchStart, "yyyy-MM-dd'T'HH:mm:ss"),
            format(fetchEnd, "yyyy-MM-dd'T'HH:mm:ss")
          ),
          MainService.getOdysseyEventStatisticsByDateRange(
            format(fetchStart, "yyyy-MM-dd'T'HH:mm:ss"),
            format(fetchEnd, "yyyy-MM-dd'T'HH:mm:ss")
          ),
        ])

        const eventStatisticsSet = [
          ...(responseEventStatistics.results ?? []),
          ...(responseOdysseyStatistics.results ?? []),
        ]

        // Generate events from robots
        const eventsSet = generateEventsFromRobots(fetchStart, fetchEnd)

        // Add event statistics
        addEventsStatistics(
          eventsSet,
          eventStatisticsSet,
          dataCache.current.robots,
          dataCache.current.missionsMap,
          dataCache.current.robotIdToFacilityIds
        )

        setCalendarEvents(eventsSet)
        setEventsLoaded(true)
        isLoadingEvents.current = false
        return eventsSet
      } catch (error) {
        console.error('Failed to generate events', error)
        isLoadingEvents.current = false
        return []
      }
    },
    [refreshTaskScheduleCount, generateEventsFromRobots, fetchInitialData]
  )

  const lastFetchedRange = useRef<{ start: number; end: number; refreshCount: number }>({
    start: 0,
    end: 0,
    refreshCount: -1,
  })

  React.useEffect(() => {
    const s = currentRange.start.getTime()
    const e = currentRange.end.getTime()
    if (
      s !== e &&
      (s !== lastFetchedRange.current.start ||
        e !== lastFetchedRange.current.end ||
        refreshTaskScheduleCount !== lastFetchedRange.current.refreshCount)
    ) {
      lastFetchedRange.current = { start: s, end: e, refreshCount: refreshTaskScheduleCount }
      getRemoteEvents(currentRange.start, currentRange.end)
    }
  }, [currentRange, getRemoteEvents, refreshTaskScheduleCount])

  const user = useUser()
  const [deleteReason, setDeleteReason] = useState<string>('')
  const [deleteDescription, setDeleteDescription] = React.useState('')

  const handleSubmitDeleteSchedule = async (): Promise<void> => {
    try {
      if (!selectedEvent) {
        throw new Error(`The selected event is null`)
      }
      const foundTask = getTaskById(selectedEvent.event_id as string)
      if (eventScope === EventScopes.CURRENT && foundTask?.scheduler_settings?.weekdays) {
        if (foundTask && foundTask.scheduler_settings) {
          const dateStr = toISOStringYYYYMMDD(selectedEvent.start)
          let new_scheduler_settings = {
            ...foundTask.scheduler_settings,
            except_days: [...foundTask.scheduler_settings.except_days],
            except_days_descriptions: { ...foundTask.scheduler_settings.except_days_descriptions },
            except_days_users: { ...(foundTask.scheduler_settings.except_days_users || {}) },
          }
          new_scheduler_settings.except_days.push(dateStr)
          new_scheduler_settings.except_days.sort()
          const finalDescription = deleteReason === 'Other' ? deleteDescription : deleteReason
          new_scheduler_settings.except_days_descriptions[dateStr] = finalDescription

          const userName = user?.state.firstName
            ? `${user.state.firstName} ${user.state.lastName || ''}`.trim()
            : user?.state.email || 'Unknown User'
          new_scheduler_settings.except_days_users[dateStr] = userName

          if ('mission_id' in foundTask) {
            await RobotApi.updateTheEntireJob(
              getTaskId(selectedEvent.event_id as string),
              foundTask.mission_id,
              foundTask.robot_id,
              new_scheduler_settings,
              foundTask.name ? foundTask.name : 'Cleaning Task'
            )
          } else {
            await RobotApi.updateTheEntireOdyssey(
              getTaskId(selectedEvent.event_id as string),
              foundTask.missions,
              foundTask.robot_id,
              new_scheduler_settings,
              foundTask.name ? foundTask.name : 'Cleaning Task',
              foundTask.scenario,
              MISSION_JOB_STATUSES.NEW
            )
          }
        }
      } else {
        if (foundTask) {
          if ('mission_id' in foundTask) {
            await RobotApi.updateMissionJobType(foundTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
          } else {
            await RobotApi.updateOdysseyType(foundTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
          }
        } else {
          console.error(`There is not task with ID ${selectedEvent.event_id} to delete!`)
        }
      }
      setOpenDeleteScheduleDialog(false)
      setDeleteDescription('')
      setDeleteReason('')
      setSelectedEvent(null)
    } catch (e) {
      console.error(`Failed to delete scheduled task: ${e}`)
    }
    setRefreshTaskScheduleCount(oldValue => ++oldValue)
    setEventScope(EventScopes.CURRENT)
  }

  const theme = useTheme()

  const eventPropGetter = useCallback(
    (event: any) => ({
      style: {
        backgroundColor: event.color || '#3174ad',
        borderRadius: '0',
        opacity: view === Views.AGENDA ? 0.9 : 1,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }),
    [view]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverEvent, setPopoverEvent] = useState<any | null>(null)

  const handleEventClick = (event: any, e: React.SyntheticEvent) => {
    const target = (e.currentTarget || e.target) as HTMLElement
    setAnchorEl(target)
    setPopoverEvent(event)
    setSelectedEvent(event)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setPopoverEvent(null)
  }

  const filterOptions = React.useMemo(() => {
    const reachableFacilityIds = new Set<number>()
    const reachableVendors = new Set<VendorCode>()
    const reachableRobots = new Map<number, any>()
    const reachableMissions = new Map<number, any>()

    const matches = (
      event: ProcessedEvent,
      opts: {
        ignoreFacility?: boolean
        ignoreVendor?: boolean
        ignoreRobot?: boolean
        ignoreMission?: boolean
      } = {}
    ) => {
      const props = event.extendedProps || {}

      if (!opts.ignoreFacility && filterFacilityId !== 'all') {
        const fid = Number(filterFacilityId)
        const fids = (props.facility_ids as number[] | undefined) || []
        if (!fids.includes(fid)) return false
      }

      if (!opts.ignoreVendor && filterVendor !== 'all' && props.vendor !== filterVendor) return false

      if (!opts.ignoreRobot && filterRobot && props.robot_id !== filterRobot.id) return false

      if (!opts.ignoreMission && filterMission) {
        const id = filterMission.id
        const missionIds = [
          ...(props.mission_id ? [props.mission_id] : []),
          ...(props.missions || []),
          ...(props.missions_info?.map((m: any) => m.id) || []),
        ]
        if (!missionIds.includes(id)) return false
      }

      return true
    }

    calendarEvents.forEach(event => {
      const props = event.extendedProps || {}

      if (matches(event, { ignoreFacility: true }) && props.facility_ids?.length) {
        ;(props.facility_ids as number[]).forEach((fid: number) => reachableFacilityIds.add(fid))
      }

      if (matches(event, { ignoreVendor: true }) && props.vendor) {
        reachableVendors.add(props.vendor as VendorCode)
      }

      if (matches(event, { ignoreRobot: true }) && props.robot_id) {
        reachableRobots.set(props.robot_id, {
          id: props.robot_id,
          name: props.robot_name || '',
          sn: props.robot_sn || '',
        })
      }

      if (matches(event, { ignoreMission: true })) {
        if (props.mission_id && props.mission_name) {
          reachableMissions.set(props.mission_id, { id: props.mission_id, name: props.mission_name })
        }
        if (props.missions_info) {
          props.missions_info.forEach((m: any) => reachableMissions.set(m.id, { id: m.id, name: m.name }))
        }
        if (props.missions) {
          props.missions.forEach((mId: number) => {
            if (!reachableMissions.has(mId)) {
              const name = dataCache.current.missionsMap.get(mId)
              if (name) reachableMissions.set(mId, { id: mId, name })
            }
          })
        }
      }
    })

    const facilityOptions = dataCache.current.facilities
      .filter(f => reachableFacilityIds.has(f.id))
      .map(f => ({ id: f.id, name: f.name || `Facility ${f.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return {
      robots: Array.from(reachableRobots.values()).sort((a, b) => a.name.localeCompare(b.name)),
      missions: Array.from(reachableMissions.values()).sort((a, b) => a.name.localeCompare(b.name)),
      facilityOptions,
      vendors: Array.from(reachableVendors).sort(),
    }
  }, [calendarEvents, filterFacilityId, filterVendor, filterRobot, filterMission])

  React.useEffect(() => {
    if (filterFacilityId !== 'all' && !filterOptions.facilityOptions.some(f => String(f.id) === filterFacilityId)) {
      setFilterFacilityId('all')
    }

    if (filterVendor !== 'all' && !filterOptions.vendors.includes(filterVendor)) {
      setFilterVendor('all')
    }

    if (filterRobot && !filterOptions.robots.find(r => r.id === filterRobot.id)) {
      setFilterRobot(null)
    }

    if (filterMission && !filterOptions.missions.find(m => m.id === filterMission.id)) {
      setFilterMission(null)
    }
  }, [filterOptions, filterFacilityId, filterVendor, filterRobot, filterMission])

  const filteredCalendarEvents = React.useMemo(() => {
    if (!calendarEvents.length) return []
    return calendarEvents.filter(event => {
      const props = event.extendedProps || {}

      if (filterFacilityId !== 'all') {
        const fid = Number(filterFacilityId)
        const fids = (props.facility_ids as number[] | undefined) || []
        if (!fids.includes(fid)) return false
      }

      if (filterVendor !== 'all' && props.vendor !== filterVendor) return false

      if (filterRobot && props.robot_id !== filterRobot.id) return false

      if (filterMission) {
        const id = filterMission.id
        const missionMatches =
          props.mission_id === id || props.missions?.includes(id) || props.missions_info?.some((m: any) => m.id === id)
        if (!missionMatches) return false
      }

      if (filterStartAfter !== -1) {
        const startHour = event.start.getHours()
        if (startHour < filterStartAfter) return false
      }

      return true
    })
  }, [calendarEvents, filterFacilityId, filterVendor, filterRobot, filterMission, filterStartAfter])
  const agendaEvents = React.useMemo(() => {
    if (view !== Views.AGENDA) return filteredCalendarEvents

    // Agenda view MUST be strictly filtered to the selected day
    const dayStart = startOfDay(selectedDate).getTime()
    const dayEnd = endOfDay(selectedDate).getTime()

    return filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= dayEnd && e >= dayStart
    })
  }, [filteredCalendarEvents, view, selectedDate])

  const hasActiveFilters = React.useMemo(() => {
    return (
      filterFacilityId !== 'all' ||
      filterVendor !== 'all' ||
      filterRobot !== null ||
      filterMission !== null ||
      filterStartAfter !== -1
    )
  }, [filterFacilityId, filterVendor, filterRobot, filterMission, filterStartAfter])

  const handleClearFilters = () => {
    setFilterFacilityId('all')
    setFilterVendor('all')
    setFilterRobot(null)
    setFilterMission(null)
    setFilterStartAfter(-1)
  }

  const handleExportSchedule = () => {
    // Export should strictly match the current view range (Day, Week, or Month)
    let rangeStart = startOfDay(selectedDate)
    let rangeEnd = endOfDay(selectedDate)

    if (view === Views.WEEK) {
      rangeStart = startOfWeek(selectedDate, { weekStartsOn: 1 })
      rangeEnd = endOfWeek(selectedDate, { weekStartsOn: 1 })
    } else if (view === Views.MONTH) {
      rangeStart = startOfMonth(selectedDate)
      rangeEnd = endOfMonth(selectedDate)
    }

    const start = rangeStart.getTime()
    const end = rangeEnd.getTime()

    const exportEvents = filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= end && e >= start
    })

    exportScheduleToCSV(exportEvents)
  }

  return (
    <>
      <div className={schedulerStyle.mainView}>
        <div className={schedulerStyle.titleBlock}>
          <Title text={'Schedule'} />
          <div style={{ display: 'flex', gap: '16px' }}>
            <CustomButton.Outlined className={style.button} style={{ width: '150px' }} onClick={handleExportSchedule}>
              Export Schedule
            </CustomButton.Outlined>
            <CustomButton.Standart
              className={style.button}
              style={{ width: '250px' }}
              onClick={() => {
                setIsSettingsOpen(true)
                setSelectedEvent(null)
              }}
            >
              Add a Task
            </CustomButton.Standart>
          </div>
        </div>

        <div className={schedulerStyle.filtersBlock}>
          <div className={schedulerStyle.filters}>
            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterFacility}`}
              value={filterFacilityId}
              onChange={e => setFilterFacilityId(e.target.value)}
            >
              <option value="all">All facilities</option>
              {filterOptions.facilityOptions.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterVendor}`}
              value={filterVendor}
              onChange={e => setFilterVendor(e.target.value as 'all' | VendorCode)}
            >
              <option value="all">All vendors</option>
              {filterOptions.vendors.map(v => (
                <option key={v} value={v}>
                  {getVendorDisplayName(v)}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterRobot}`}
              value={filterRobot?.id || ''}
              onChange={e => {
                const id = Number(e.target.value)
                const robot = filterOptions.robots.find(r => r.id === id) || null
                setFilterRobot(robot)
              }}
            >
              <option value="">All Robots</option>
              {filterOptions.robots.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name || r.sn}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterMission}`}
              value={filterMission?.id || ''}
              onChange={e => {
                const id = Number(e.target.value)
                const mission = filterOptions.missions.find(m => m.id === id) || null
                setFilterMission(mission)
              }}
            >
              <option value="">All Missions</option>
              {filterOptions.missions.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterTime}`}
              value={filterStartAfter}
              onChange={e => setFilterStartAfter(Number(e.target.value))}
            >
              <option value={-1}>Start After: All</option>
              {Array.from({ length: 24 }).map((_, i) => (
                <option key={i} value={i}>
                  Start After: {i < 10 ? `0${i}` : i}:00
                </option>
              ))}
            </select>

            <CustomButton.Outlined
              className={schedulerStyle.clearButton}
              onClick={handleClearFilters}
              style={{ borderRadius: 0 }}
            >
              Clear Filters
            </CustomButton.Outlined>
          </div>
        </div>
        <div className={schedulerStyle.calendarWrapper}>
          <Calendar
            localizer={localizer}
            events={agendaEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            view={view}
            onView={handleViewChange}
            date={selectedDate}
            onNavigate={handleNavigate}
            length={view === Views.AGENDA ? 1 : undefined}
            onRangeChange={(range: any) => {
              const newStart = Array.isArray(range) ? range[0] : range.start
              const newEnd = Array.isArray(range) ? range[range.length - 1] : range.end

              if (newStart && newEnd) {
                const s = newStart.getTime()
                const e = newEnd.getTime()

                setCurrentRange(prev => {
                  if (prev.start.getTime() === s && prev.end.getTime() === e) {
                    return prev
                  }
                  return { start: new Date(s), end: new Date(e) }
                })
              }
            }}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            components={{
              event: ({ event }: { event: any }) => (
                <div
                  title={event.title}
                  style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' }}
                >
                  {event.title}
                </div>
              ),
            }}
          />
        </div>

        <Popover
          open={Boolean(anchorEl) && Boolean(popoverEvent)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              p: 0,
              borderRadius: 2,
              minWidth: 350,
              maxWidth: 450,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          }}
        >
          {popoverEvent && (
            <Box>
              <Box sx={{ height: '6px', bgcolor: popoverEvent.color || '#3174ad' }} />
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', lineHeight: 1.2 }}>
                    {popoverEvent.title}
                  </Typography>
                  <IconButton size="small" onClick={handleClosePopover} sx={{ mt: -0.5, mr: -0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <TaskEventViewer event={popoverEvent} />

                <Divider sx={{ my: 2, borderColor: '#f0f0f0' }} />
                {popoverEvent.end.getTime() > Date.now() && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon fontSize="small" />}
                      sx={{ borderRadius: 0, textTransform: 'none', px: 2 }}
                      onClick={() => {
                        handleClosePopover()
                        const isSingle = isAllValueDisabled(popoverEvent)
                        if (isSingle) {
                          setSingleTaskEditing(true)
                          setIsSettingsOpen(true)
                          setEventScope(EventScopes.CURRENT)
                        } else {
                          setIsEditConfirmationOpen(true)
                        }
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon fontSize="small" />}
                      sx={{ borderRadius: 0, textTransform: 'none', px: 2 }}
                      onClick={() => {
                        handleClosePopover()
                        setDeleteDescription('')
                        setDeleteReason('')
                        setOpenDeleteScheduleDialog(true)
                      }}
                    >
                      {t('common.delete')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Popover>

        {isEditConfirmationOpen && (
          <ConfirmationDialogAllSingle
            confirmText={'Ok'}
            cancelText="Cancel"
            open={true}
            title={'Edit the scheduled task?'}
            submitting={undefined}
            onClose={() => {
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
              setSelectedEvent(null)
            }}
            onSubmit={() => {
              if (eventScope === EventScopes.ALL) setSingleTaskEditing(false)
              else if (eventScope === EventScopes.CURRENT) setSingleTaskEditing(true)
              else throw new Error(`Unknown event scope: ${eventScope}`)
              setIsSettingsOpen(true)
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
            }}
          >
            <EventEditDeletePopup
              currentValue={EventScopes.CURRENT}
              allValue={EventScopes.ALL}
              value={eventScope}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
            />
          </ConfirmationDialogAllSingle>
        )}

        {
          <TaskSettingsModal
            isOpen={isSettingsOpen}
            onClose={onSettingsCloseClicked}
            taskName={'Cleaning Task'}
            taskToEdit={selectedEvent ? getTaskById(selectedEvent.event_id as string) : undefined}
            eventScope={isSingleTaskEditing ? 'current' : 'all'}
            dateToEdit={isSingleTaskEditing && selectedEvent ? selectedEvent.start : null}
            onSubmit={onSettingsSubmitClicked}
          />
        }
        {openDeleteScheduleDialog && (
          <ConfirmationDialogAllSingle
            confirmText={isAllValueDisabled(selectedEvent) ? t('common.delete') : 'Ok'}
            cancelText="Cancel"
            open={openDeleteScheduleDialog}
            isSmall={isAllValueDisabled(selectedEvent)}
            danger={true}
            title={isAllValueDisabled(selectedEvent) ? 'Delete the task?' : 'Delete the scheduled task?'}
            disableConfirm={
              !isAllValueDisabled(selectedEvent) &&
              eventScope === EventScopes.CURRENT &&
              (!deleteReason || (deleteReason === 'Other' && !deleteDescription))
            }
            submitting={undefined}
            onClose={() => {
              setOpenDeleteScheduleDialog(false)
              setEventScope(EventScopes.CURRENT)
              setDeleteDescription('')
              setDeleteReason('')
              setSelectedEvent(null)
            }}
            onSubmit={handleSubmitDeleteSchedule}
          >
            {!isAllValueDisabled(selectedEvent) && (
              <>
                <EventEditDeletePopup
                  currentValue={EventScopes.CURRENT}
                  allValue={EventScopes.ALL}
                  value={eventScope}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
                />
                {eventScope === EventScopes.CURRENT && (
                  <Box sx={{ marginBottom: '30px' }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="delete-reason-label">Reason</InputLabel>
                      <Select
                        labelId="delete-reason-label"
                        value={deleteReason}
                        label="Reason"
                        onChange={e => setDeleteReason(e.target.value)}
                      >
                        <MenuItem value="Weather Conditions">Weather Conditions</MenuItem>
                        <MenuItem value="Busy Area">Busy Area</MenuItem>
                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    {deleteReason === 'Other' && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={deleteDescription}
                        onChange={e => setDeleteDescription(e.target.value)}
                        placeholder="Please provide a reason..."
                        required
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </ConfirmationDialogAllSingle>
        )}
      </div>
    </>
  )
}

export default TaskScheduler
